/*
 * Formulario completo de una visita IGI: checklist fijo de 9 puntos, objetivo/diagnostico/decision,
 * acciones y compromisos (filas dinamicas, se agregan/quitan en el navegador), reflexion y ritual de
 * seguimiento. Se guarda SOLO con el boton Guardar (sin autoguardado); reemplaza la visita completa
 * (no hay edicion parcial como en evaluaciones). Con cambios sin guardar avisa al salir: el Prompt de
 * react-router vive en tienda/index.js (una sola instancia para toda la ficha, via `onCambios`, igual
 * que Evaluar); el beforeunload queda aca, independiente, igual que evaluar.js.
 */
import React, {useEffect, useState} from "react";
import {useDispatch, useSelector} from "react-redux";
import {toast} from "react-toastify";
import Button from "../../../../components/button";
import LoadingSpinner from "../../../../components/loading-spinner";
import {
    clear_igi,
    create_visita,
    update_visita,
    CREATE_VISITA_SUCCESS,
    UPDATE_VISITA_SUCCESS,
} from "../../../../redux/igi";

const MENSAJE_SALIDA = "Tiene cambios sin guardar en la visita. ¿Desea salir sin guardar?";

const CHECKLIST = [
    ["visito", "Visitó la joyería"],
    ["reviso_resultados", "Revisó resultados"],
    ["ejecuto_activacion", "Ejecutó activación comercial"],
    ["feedback", "Dio feedback puntual"],
    ["reconocio", "Reconoció a un colaborador"],
    ["detecto_brecha", "Detectó brecha concreta"],
    ["dejo_tareas", "Dejó tareas escritas"],
    ["pidio_evidencia", "Pidió evidencia"],
    ["seguimiento_ant", "Hizo seguimiento a compromisos anteriores"],
];
const ACCION_ESTADOS = ["Pendiente", "En progreso", "Completado", "Bloqueado"];
const COMPROMISO_ESTADOS = ["Pendiente", "En proceso", "Hecho"];
const hoy = () => {
    const ahora = new Date();
    const mes = String(ahora.getMonth() + 1).padStart(2, "0");
    const dia = String(ahora.getDate()).padStart(2, "0");
    return `${ahora.getFullYear()}-${mes}-${dia}`;
};

const estadoInicial = (visita) => ({
    fecha: visita ? visita.fecha : hoy(),
    hora_inicio: visita ? (visita.hora_inicio || "") : "",
    hora_fin: visita ? (visita.hora_fin || "") : "",
    objetivo: visita ? visita.objetivo : "",
    que_pasa: visita ? visita.que_pasa : "",
    desafio: visita ? visita.desafio : "",
    decision: visita ? visita.decision : "",
    reflexion: visita ? visita.reflexion : "",
    checklist: visita ? {...visita.checklist} : {},
    acciones: visita ? visita.acciones.map((a) => ({...a})) : [],
    compromisos: visita ? visita.compromisos.map((c) => ({...c})) : [],
    ritual: visita ? {...visita.ritual} : {semana: "", dijimos: "", se_hizo: "", aprendimos: "", acuerdos: ""},
});

const VisitaFormulario = ({token, tiendaId, visita, onGuardado, onCancelar, onCambios}) => {
    const dispatch = useDispatch();
    const _create = useSelector((state) => state.igi_module._create_visita);
    const _update = useSelector((state) => state.igi_module._update_visita);
    const [form, setForm] = useState(() => estadoInicial(visita));
    const [conCambios, setConCambios] = useState(false);
    const [guardando, setGuardando] = useState(false);

    useEffect(() => {
        dispatch(clear_igi(CREATE_VISITA_SUCCESS));
        dispatch(clear_igi(UPDATE_VISITA_SUCCESS));
    }, [dispatch]);

    useEffect(() => {
        onCambios(conCambios);
    }, [conCambios, onCambios]);

    useEffect(() => {
        if (!conCambios) {
            return undefined;
        }
        const avisar = (e) => {
            e.preventDefault();
            e.returnValue = MENSAJE_SALIDA;
            return MENSAJE_SALIDA;
        };
        window.addEventListener("beforeunload", avisar);
        return () => window.removeEventListener("beforeunload", avisar);
    }, [conCambios]);

    useEffect(() => {
        const resultado = visita ? _update : _create;
        if (!guardando || resultado.status === 0) {
            return;
        }
        dispatch(clear_igi(visita ? UPDATE_VISITA_SUCCESS : CREATE_VISITA_SUCCESS));
        setGuardando(false);
        if (resultado.status === 200 || resultado.status === 201) {
            toast.success(visita ? "La visita se actualizó correctamente" : "La visita se guardó correctamente");
            setConCambios(false);
            onGuardado();
        } else {
            toast.error(resultado.message || "No se pudo guardar la visita");
        }
    }, [_create, _update, guardando, visita, dispatch, onGuardado]);

    const cambiar = (campo, valor) => {
        setForm((prev) => ({...prev, [campo]: valor}));
        setConCambios(true);
    };
    const cambiarChecklist = (clave, valor) => cambiar("checklist", {...form.checklist, [clave]: valor});
    const cambiarRitual = (campo, valor) => cambiar("ritual", {...form.ritual, [campo]: valor});

    const agregarAccion = () => cambiar("acciones", [
        ...form.acciones, {tema: "", accion: "", responsable: "", fecha: "", estado: "Pendiente"},
    ]);
    const quitarAccion = (i) => cambiar("acciones", form.acciones.filter((_, idx) => idx !== i));
    const cambiarAccion = (i, campo, valor) => cambiar("acciones",
        form.acciones.map((a, idx) => (idx === i ? {...a, [campo]: valor} : a)));

    const agregarCompromiso = () => cambiar("compromisos", [
        ...form.compromisos, {compromiso: "", accion: "", estado: "Pendiente", evidencia: ""},
    ]);
    const quitarCompromiso = (i) => cambiar("compromisos", form.compromisos.filter((_, idx) => idx !== i));
    const cambiarCompromiso = (i, campo, valor) => cambiar("compromisos",
        form.compromisos.map((c, idx) => (idx === i ? {...c, [campo]: valor} : c)));

    const cancelar = () => {
        if (conCambios && !window.confirm(MENSAJE_SALIDA)) {
            return;
        }
        setConCambios(false);
        onCancelar();
    };

    const guardar = (e) => {
        e.preventDefault();
        if (!form.fecha) {
            toast.error("La fecha de la visita es obligatoria");
            return;
        }
        const payload = {
            fecha: form.fecha,
            hora_inicio: form.hora_inicio || null,
            hora_fin: form.hora_fin || null,
            objetivo: form.objetivo,
            que_pasa: form.que_pasa,
            desafio: form.desafio,
            decision: form.decision,
            reflexion: form.reflexion,
            checklist: form.checklist,
            acciones: form.acciones.map((a) => ({...a, fecha: a.fecha || null})),
            compromisos: form.compromisos,
            ritual: form.ritual,
        };
        setGuardando(true);
        if (visita) {
            dispatch(update_visita(token, visita.id, payload));
        } else {
            dispatch(create_visita(token, tiendaId, payload));
        }
    };

    return (
        <form onSubmit={guardar}>
            {guardando && <LoadingSpinner/>}
            <div className="alert alert-warning d-flex justify-content-between align-items-center">
                <span>{visita ? "Editando visita" : "Nueva visita"}</span>
                <Button type="button" theme="light" classes="btn-sm" text="Cancelar" onChange={cancelar}/>
            </div>
            <div className="card mb-1">
                <div className="card-body p-1">
                    <div className="form-row">
                        <div className="form-group col-6">
                            <label htmlFor="visita-fecha">Fecha</label>
                            <input id="visita-fecha" type="date" className="form-control"
                                value={form.fecha} onChange={(e) => cambiar("fecha", e.target.value)}/>
                        </div>
                        <div className="form-group col-3">
                            <label htmlFor="visita-hi">Hora inicio</label>
                            <input id="visita-hi" type="time" className="form-control"
                                value={form.hora_inicio} onChange={(e) => cambiar("hora_inicio", e.target.value)}/>
                        </div>
                        <div className="form-group col-3">
                            <label htmlFor="visita-hf">Hora fin</label>
                            <input id="visita-hf" type="time" className="form-control"
                                value={form.hora_fin} onChange={(e) => cambiar("hora_fin", e.target.value)}/>
                        </div>
                    </div>
                    <div className="form-group">
                        <label htmlFor="visita-objetivo">¿Qué iba a revisar / lograr?</label>
                        <textarea id="visita-objetivo" className="form-control" rows={2}
                            value={form.objetivo} onChange={(e) => cambiar("objetivo", e.target.value)}/>
                    </div>
                    <div className="form-group">
                        <label htmlFor="visita-que-pasa">¿Qué está pasando?</label>
                        <textarea id="visita-que-pasa" className="form-control" rows={2}
                            value={form.que_pasa} onChange={(e) => cambiar("que_pasa", e.target.value)}/>
                    </div>
                    <div className="form-group">
                        <label htmlFor="visita-desafio">¿Cuál es el desafío?</label>
                        <textarea id="visita-desafio" className="form-control" rows={2}
                            value={form.desafio} onChange={(e) => cambiar("desafio", e.target.value)}/>
                    </div>
                    <div className="form-group">
                        <label htmlFor="visita-decision">Decisión tomada</label>
                        <textarea id="visita-decision" className="form-control" rows={2}
                            value={form.decision} onChange={(e) => cambiar("decision", e.target.value)}/>
                    </div>
                </div>
            </div>

            <div className="card mb-1">
                <div className="card-header p-1"><h5 className="card-title mb-0">Lista de verificación</h5></div>
                <div className="card-body p-1">
                    {CHECKLIST.map(([clave, etiqueta]) => (
                        <div key={clave} className="custom-control custom-checkbox mb-50">
                            <input
                                type="checkbox"
                                className="custom-control-input"
                                id={`check-${clave}`}
                                checked={!!form.checklist[clave]}
                                onChange={(e) => cambiarChecklist(clave, e.target.checked)}
                            />
                            <label className="custom-control-label" htmlFor={`check-${clave}`}>{etiqueta}</label>
                        </div>
                    ))}
                </div>
            </div>

            <div className="card mb-1">
                <div className="card-header p-1 d-flex justify-content-between align-items-center">
                    <h5 className="card-title mb-0">Acciones acordadas</h5>
                    <Button type="button" theme="light" classes="btn-sm" text="+ Agregar" onChange={agregarAccion}/>
                </div>
                <div className="card-body p-1">
                    {form.acciones.length === 0 && <div className="text-muted small">Sin acciones.</div>}
                    {form.acciones.map((a, i) => (
                        <div key={i} className="border-bottom py-50">
                            <div className="form-row">
                                <div className="col-12 col-md-3 mb-50">
                                    <input className="form-control form-control-sm" placeholder="Tema"
                                        value={a.tema} onChange={(e) => cambiarAccion(i, "tema", e.target.value)}/>
                                </div>
                                <div className="col-12 col-md-3 mb-50">
                                    <input className="form-control form-control-sm" placeholder="Acción"
                                        value={a.accion} onChange={(e) => cambiarAccion(i, "accion", e.target.value)}/>
                                </div>
                                <div className="col-12 col-md-2 mb-50">
                                    <input className="form-control form-control-sm" placeholder="Responsable"
                                        value={a.responsable}
                                        onChange={(e) => cambiarAccion(i, "responsable", e.target.value)}/>
                                </div>
                                <div className="col-6 col-md-2 mb-50">
                                    <input type="date" className="form-control form-control-sm"
                                        value={a.fecha || ""} onChange={(e) => cambiarAccion(i, "fecha", e.target.value)}/>
                                </div>
                                <div className="col-5 col-md-1 mb-50">
                                    <select className="form-control form-control-sm" value={a.estado}
                                        onChange={(e) => cambiarAccion(i, "estado", e.target.value)}>
                                        {ACCION_ESTADOS.map((op) => <option key={op} value={op}>{op}</option>)}
                                    </select>
                                </div>
                                <div className="col-1 mb-50 text-right">
                                    <button type="button" className="btn btn-sm btn-danger"
                                        onClick={() => quitarAccion(i)}>✕</button>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            <div className="card mb-1">
                <div className="card-header p-1 d-flex justify-content-between align-items-center">
                    <h5 className="card-title mb-0">Compromisos</h5>
                    <Button type="button" theme="light" classes="btn-sm" text="+ Agregar" onChange={agregarCompromiso}/>
                </div>
                <div className="card-body p-1">
                    {form.compromisos.length === 0 && <div className="text-muted small">Sin compromisos.</div>}
                    {form.compromisos.map((c, i) => (
                        <div key={i} className="border-bottom py-50">
                            <div className="form-row">
                                <div className="col-12 col-md-4 mb-50">
                                    <input className="form-control form-control-sm" placeholder="Compromiso"
                                        value={c.compromiso}
                                        onChange={(e) => cambiarCompromiso(i, "compromiso", e.target.value)}/>
                                </div>
                                <div className="col-12 col-md-4 mb-50">
                                    <input className="form-control form-control-sm" placeholder="Acción definida"
                                        value={c.accion} onChange={(e) => cambiarCompromiso(i, "accion", e.target.value)}/>
                                </div>
                                <div className="col-6 col-md-2 mb-50">
                                    <select className="form-control form-control-sm" value={c.estado}
                                        onChange={(e) => cambiarCompromiso(i, "estado", e.target.value)}>
                                        {COMPROMISO_ESTADOS.map((op) => <option key={op} value={op}>{op}</option>)}
                                    </select>
                                </div>
                                <div className="col-5 col-md-1 mb-50">
                                    <input className="form-control form-control-sm" placeholder="Evidencia"
                                        value={c.evidencia}
                                        onChange={(e) => cambiarCompromiso(i, "evidencia", e.target.value)}/>
                                </div>
                                <div className="col-1 mb-50 text-right">
                                    <button type="button" className="btn btn-sm btn-danger"
                                        onClick={() => quitarCompromiso(i)}>✕</button>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            <div className="card mb-1">
                <div className="card-body p-1">
                    <div className="form-group">
                        <label htmlFor="visita-reflexion">Reflexión del jefe zonal</label>
                        <textarea id="visita-reflexion" className="form-control" rows={2}
                            value={form.reflexion} onChange={(e) => cambiar("reflexion", e.target.value)}/>
                    </div>
                </div>
            </div>

            <div className="card mb-1">
                <div className="card-header p-1"><h5 className="card-title mb-0">Ritual de seguimiento (15 min)</h5></div>
                <div className="card-body p-1">
                    <div className="form-group">
                        <label htmlFor="ritual-semana">Semana</label>
                        <input id="ritual-semana" className="form-control"
                            value={form.ritual.semana} onChange={(e) => cambiarRitual("semana", e.target.value)}/>
                    </div>
                    <div className="form-group">
                        <label htmlFor="ritual-dijimos">1. ¿Qué dijimos que íbamos a hacer?</label>
                        <textarea id="ritual-dijimos" className="form-control" rows={2}
                            value={form.ritual.dijimos} onChange={(e) => cambiarRitual("dijimos", e.target.value)}/>
                    </div>
                    <div className="form-group">
                        <label htmlFor="ritual-se-hizo">2. ¿Qué sí se hizo?</label>
                        <textarea id="ritual-se-hizo" className="form-control" rows={2}
                            value={form.ritual.se_hizo} onChange={(e) => cambiarRitual("se_hizo", e.target.value)}/>
                    </div>
                    <div className="form-group">
                        <label htmlFor="ritual-aprendimos">3. ¿Qué aprendimos?</label>
                        <textarea id="ritual-aprendimos" className="form-control" rows={2}
                            value={form.ritual.aprendimos} onChange={(e) => cambiarRitual("aprendimos", e.target.value)}/>
                    </div>
                    <div className="form-group">
                        <label htmlFor="ritual-acuerdos">Acuerdos próxima semana</label>
                        <textarea id="ritual-acuerdos" className="form-control" rows={2}
                            value={form.ritual.acuerdos} onChange={(e) => cambiarRitual("acuerdos", e.target.value)}/>
                    </div>
                </div>
            </div>

            <div style={{position: "sticky", bottom: 0, background: "#fff", padding: "0.5rem 0", zIndex: 5}}>
                <Button
                    type="submit"
                    theme="success"
                    classes="btn-block"
                    disabled={guardando}
                    text={<><i className="bx bx-save"/> Guardar</>}
                />
            </div>
        </form>
    );
};

export default VisitaFormulario;
